import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useRealmApp } from "src/store/RealmApp";
import { ProjectsV2FilterGroup } from "../Services/Projects/projects";
import { TaskData } from "../Services/Tasks/tasks";
import { useAccessToken } from "../useAccessToken";
import { InternalPhaseUpdateData } from "../Services/SharedSpace/phases";

export const projectAnalyticsKeys = {
  performance: () => ["analytics", "project", "performance"] as const,
  stageDistribution: (projectTemplateId: string) =>
    ["analytics", "project", projectTemplateId, "stage-distribution"] as const,
  milestones: (filter: ProjectsV2FilterGroup, page: number) =>
    ["analytics", "project", "milestones", filter, page] as const,
  progressAndDates: (filter: ProjectsV2FilterGroup) =>
    ["analytics", "project", "progress-and-dates", filter] as const,
  completedProjectsDistribution: (filter: ProjectsV2FilterGroup) =>
    [
      "analytics",
      "project",
      "completed-projects-distribution",
      filter,
    ] as const,
  projectProgressComparison: (
    filter: ProjectsV2FilterGroup,
    baselineDate: string,
    page: number,
    sort: ProjectProgressComparisonSort
  ) =>
    [
      "analytics",
      "project",
      "project-progress-comparison",
      filter,
      baselineDate,
      page,
      sort,
    ] as const,
};

export type ProjectPerformanceData = {
  _id: string;
  name: string;
  type: "Customer Onboarding";
  revenue?: number;
  ownerUser: {
    _id: string;
    name: string;
    email: string;
  };
  isActive: boolean;
  createdAt: string;
  customer: {
    _id: string;
    name: string;
    revenue?: number;
  };
  sharedSpace: {
    _id: string;
    name: string;
  };
  journey: {
    _id: string;
    name: string;
  };
  template: {
    _id: string;
    name: string;
  };
  keyDates: {
    startedDate: string;
    plannedCompletionDate: string;
    forecastedCompletionDate: string;
    completionDate: string;
  };
  allTasksBreakdown: {
    internal: TasksBreakdown;
    external: TasksBreakdown;
  };
  userTasksBreakdown: TasksBreakdown;
};

type TasksBreakdown = {
  toDo: {
    total: number;
    overdue: number;
  };
  inProgress: {
    total: number;
    overdue: number;
  };
  completed: {
    onTime: number;
    late: number;
  };
  canceled: number;
};

export type ProjectsMilestonesAnalyticsData = {
  _id: string;
  name: string;
  assignedStartDate?: string;
  assignedEndDate?: string;
  createdAt: string;
  milestones: (TaskData & { expectedCompletionDate?: string })[];
};

export const useGetProjectsMilestonesAnalytics = (
  filter: ProjectsV2FilterGroup,
  page: number
) => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    projectAnalyticsKeys.milestones(filter, page),
    async (): Promise<{
      projects: ProjectsMilestonesAnalyticsData[];
      pagination: {
        currentPage: number;
        pageSize: number;
        totalPages: number;
        totalRecords: number;
      };
    }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/milestonesForProjects`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            instanceId: app.currentUser.customData.instanceId.$oid,
            filter: JSON.stringify(filter),
            page,
            limit: 15,
          },
        }
      );
      return res.data;
    },
    {
      staleTime: Infinity,
    }
  );
};

export type ProjectProgressAndDatesData = {
  _id: string;
  name: string;
  closedTasks: number;
  totalTasks: number;
  assignedStartDate?: string;
  createdAt: string;
};

export const useGetProjectProgressAndDates = (
  filter: ProjectsV2FilterGroup
) => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    projectAnalyticsKeys.progressAndDates(filter),
    async (): Promise<ProjectProgressAndDatesData[]> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/projectsProgressAndDates`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            instanceId: app.currentUser.customData.instanceId.$oid,
            filter: JSON.stringify(filter),
          },
        }
      );
      return res.data;
    },
    {
      staleTime: Infinity,
    }
  );
};

export type CompletedProjectsDistributionData = {
  _id: string;
  name: string;
  status: string;
  isActive: boolean;
  createdAt: string;
  keyDates: { completionDate: string };
  assignedStartDate: string;
  assignedEndDate: string;
  customer: {
    _id: string;
    name: string;
  };
  owner: {
    _id: string;
    name: string;
    email: string;
  };
};

export const useGetAverageProjectCompletionTime = (
  baselineFilter: ProjectsV2FilterGroup,
  filter: ProjectsV2FilterGroup
) => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    projectAnalyticsKeys.completedProjectsDistribution(filter),
    async (): Promise<CompletedProjectsDistributionData[]> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/completedProjectsDistribution`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            instanceId: app.currentUser.customData.instanceId.$oid,
            baselineFilter: JSON.stringify(baselineFilter),
            filter: JSON.stringify(filter),
          },
        }
      );
      return res.data;
    },
    {
      staleTime: Infinity,
    }
  );
};

export type ProjectProgressComparisonData = {
  _id: string;
  name: string;
  status: string;
  isActive: boolean;
  createdAt: string;
  closedTasks: number;
  completedTasks: number;
  canceledTasks: number;
  totalTasks: number;
  stuckTasks: number;
  overdueTasks: number;
  projectProgress: number;
  incrementalProjectProgress: number;
  pastInternalUpdates: InternalPhaseUpdateData[];
  lastInternalUpdateStatus: InternalPhaseUpdateData["status"];
  lastInternalUpdateCreatedAt?: string;
  baselineProgress: {
    date: string;
    totalTasks: number;
    closedTasks: number;
    completedTasks: number;
    canceledTasks: number;
    milestones: {
      _id: string;
      title: string;
      status: string;
      completionDate?: string;
    }[];
  };
  targetProgress: {
    date: string;
    totalTasks: number;
    closedTasks: number;
    completedTasks: number;
    canceledTasks: number;
    milestones: {
      _id: string;
      title: string;
      status: string;
      completionDate?: string;
    }[];
  };
  completionDate: string;
  assignedStartDate: string;
  assignedEndDate: string;
  customer: {
    _id: string;
    name: string;
  };
  owner: {
    _id: string;
    name: string;
    email: string;
  };
};

export type ProjectProgressComparisonSort = {
  property:
    | "createdAt"
    | "incrementalProjectProgress"
    | "incrementalClosedTasks"
    | "incrementalTotalTasks"
    | "incrementalMilestonesCompleted"
    | "assignedStartDate"
    | "projectProgress"
    | "milestonesCompleted"
    | "lastInternalUpdateStatus"
    | "stuckTasks"
    | "overdueTasks"
    | "lastInternalUpdateCreatedAt";
  order: "asc" | "desc";
};

export const useGetProjectProgressComparison = (
  baselineFilter: ProjectsV2FilterGroup,
  filter: ProjectsV2FilterGroup,
  baselineDate: string,
  page: number,
  sort: ProjectProgressComparisonSort
) => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    projectAnalyticsKeys.projectProgressComparison(
      filter,
      baselineDate,
      page,
      sort
    ),
    async (): Promise<{
      // noOfProjects: number;
      projects: ProjectProgressComparisonData[];
      pagination: {
        currentPage: number;
        pageSize: number;
        totalPages: number;
        totalRecords: number;
        baselineCount: number;
      };
    }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/projectsProgressComparison`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            instanceId: app.currentUser.customData.instanceId.$oid,
            baselineFilter: JSON.stringify(baselineFilter),
            filter: JSON.stringify(filter),
            baselineDate,
            page,
            sort: JSON.stringify(sort),
          },
        }
      );
      return res.data;
    },
    {
      staleTime: 1000 * 60,
    }
  );
};

export const useGetProjectPerformance = (isActive: boolean = true) => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    projectAnalyticsKeys.performance(),
    async (): Promise<ProjectPerformanceData[]> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/projectPerformance`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            // isActive,
          },
        }
      );
      return res.data;
    },
    {
      staleTime: Infinity,
    }
  );
};

type StageDistributionData = {
  name: string;
  projects: {
    _id: string;
    name: string;
    customer: string;
    revenue: number;
  }[];
};

export const useGetStageDistributionForProjectTemplate = (
  projectTemplateId: string
) => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    projectAnalyticsKeys.stageDistribution(projectTemplateId),
    async (): Promise<StageDistributionData[]> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/stageDistributionForProjectTemplate`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            projectTemplateId,
          },
        }
      );
      return res.data;
    },
    {
      staleTime: Infinity,
    }
  );
};
